import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import {
  Box,
  Typography,
  Button,
  Table,
  Sheet,
  Card,
  CardContent,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Checkbox,
  Stack,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Divider,
} from '@mui/joy';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useLoading } from '../../context/LoadingContext';
import MainLayout from '../../components/Layout/MainLayout';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'viewer',
    permissions: [],
  });
  const [error, setError] = useState('');

  const { user: currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { setLoading, isLoading } = useLoading();

  const availablePermissions = [
    { id: 'manage_users', label: 'Manage Users', description: 'Create, edit, and delete users' },
    { id: 'manage_content', label: 'Manage Content', description: 'Upload and manage media content' },
    { id: 'manage_devices', label: 'Manage Devices', description: 'Add and configure TV displays' },
    { id: 'manage_layouts', label: 'Manage Layouts', description: 'Create and edit display layouts' },
    { id: 'manage_schedules', label: 'Manage Schedules', description: 'Create and manage content schedules' },
    { id: 'view_analytics', label: 'View Analytics', description: 'Access system analytics and reports' },
  ];

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading('loadUsers', true);
    try {
      const response = await api.get('api/users');

      if (response.data.success) {
        setUsers(response.data.users);
      } else {
        showError('Failed to load users');
      }
    } catch (error) {
      console.error('Load users error:', error);
      showError(error.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading('loadUsers', false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email || !formData.password) {
      setError('Name, email, and password are required');
      return;
    }

    setLoading('createUser', true);
    try {
      const response = await api.post('api/users', formData);

      if (response.data.success) {
        showSuccess('User created successfully');
        setIsCreateModalOpen(false);
        resetForm();
        loadUsers(); // Reload the users list
      } else {
        setError(response.data.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Create user error:', error);
      setError(error.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading('createUser', false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.email) {
      setError('Name and email are required');
      return;
    }

    setLoading('editUser', true);
    try {
      const updateData = { ...formData };
      delete updateData.password; // Don't send password if not changing

      const response = await api.put(
        `api/users/${selectedUser.id}`,
        updateData
      );

      if (response.data.success) {
        showSuccess('User updated successfully');
        setIsEditModalOpen(false);
        resetForm();
        loadUsers(); // Reload the users list
      } else {
        setError(response.data.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Update user error:', error);
      setError(error.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading('editUser', false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.id) {
      showError('You cannot delete your own account');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      setLoading('deleteUser', true);
      try {
        const response = await api.delete(`api/users/${userId}`);

        if (response.data.success) {
          showSuccess('User deleted successfully');
          loadUsers(); // Reload the users list
        } else {
          showError(response.data.error || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Delete user error:', error);
        showError(error.response?.data?.error || 'Failed to delete user');
      } finally {
        setLoading('deleteUser', false);
      }
    }
  };

  const handleToggleUserStatus = async (userId) => {
    setLoading('toggleStatus', true);
    try {
      const response = await api.patch(`api/users/${userId}/status`, {});

      if (response.data.success) {
        showSuccess('User status updated');
        loadUsers(); // Reload the users list
      } else {
        showError(response.data.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Toggle status error:', error);
      showError(error.response?.data?.error || 'Failed to update user status');
    } finally {
      setLoading('toggleStatus', false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'viewer',
      permissions: [],
    });
    setSelectedUser(null);
    setError('');
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      permissions: user.permissions,
    });
    setIsEditModalOpen(true);
  };

  const handlePermissionChange = (permissionId, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        permissions: [...formData.permissions, permissionId]
      });
    } else {
      setFormData({
        ...formData,
        permissions: formData.permissions.filter(p => p !== permissionId)
      });
    }
  };

  return (
    <MainLayout currentPage="users">
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography level="h2" sx={{ fontWeight: 600, mb: 1 }}>
              User Management
            </Typography>
            <Typography level="body-md" sx={{ color: 'neutral.600' }}>
              Manage system users and their permissions
            </Typography>
          </Box>

          <Button
            onClick={() => setIsCreateModalOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d16000 0%, #e67700 100%)',
              },
            }}
          >
            ➕ Create User
          </Button>
        </Box>

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" color="primary">Total Users</Typography>
              <Typography level="h2" sx={{ color: 'primary.500' }}>{users.length}</Typography>
              <Typography level="body-sm">Registered accounts</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" color="success">Active Users</Typography>
              <Typography level="h2" sx={{ color: 'success.500' }}>
                {users.filter(u => u.status === 'active').length}
              </Typography>
              <Typography level="body-sm">Currently active</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" color="warning">Admin Users</Typography>
              <Typography level="h2" sx={{ color: 'warning.500' }}>
                {users.filter(u => u.role === 'admin').length}
              </Typography>
              <Typography level="body-sm">With admin privileges</Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Users Table */}
        <Card variant="outlined">
          <Sheet sx={{ overflow: 'auto' }}>
            <Table stickyHeader>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Avatar</th>
                  <th style={{ minWidth: 150 }}>Name</th>
                  <th style={{ minWidth: 200 }}>Email</th>
                  <th style={{ width: 100 }}>Role</th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ minWidth: 250 }}>Permissions</th>
                  <th style={{ width: 150 }}>Last Login</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <Avatar
                        size="sm"
                        sx={{
                          background: user.role === 'admin'
                            ? 'linear-gradient(135deg, #eb7a24 0%, #f19b30 100%)'
                            : 'neutral.300'
                        }}
                      >
                        {user.name.charAt(0)}
                      </Avatar>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {user.name}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">
                        {user.email}
                      </Typography>
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={user.role === 'admin' ? 'warning' : user.role === 'manager' ? 'info' : 'primary'}
                      >
                        {user.role}
                      </Chip>
                    </td>
                    <td>
                      <Chip
                        size="sm"
                        variant="soft"
                        color={user.status === 'active' ? 'success' : 'neutral'}
                      >
                        {user.status}
                      </Chip>
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.permissions.slice(0, 2).map((permission) => (
                          <Chip key={permission} size="sm" variant="outlined">
                            {availablePermissions.find(p => p.id === permission)?.label}
                          </Chip>
                        ))}
                        {user.permissions.length > 2 && (
                          <Chip size="sm" variant="soft">
                            +{user.permissions.length - 2} more
                          </Chip>
                        )}
                      </Box>
                    </td>
                    <td>
                      <Typography level="body-xs">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : 'Never'
                        }
                      </Typography>
                    </td>
                    <td>
                      <Dropdown>
                        <MenuButton
                          slots={{ root: IconButton }}
                          slotProps={{ root: { variant: 'plain', size: 'sm' } }}
                        >
                          ⋮
                        </MenuButton>
                        <Menu>
                          <MenuItem onClick={() => openEditModal(user)}>
                            ✏️ Edit
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleToggleUserStatus(user.id)}
                            disabled={user.id === currentUser?.id}
                          >
                            {user.status === 'active' ? '🚫 Deactivate' : '✅ Activate'}
                          </MenuItem>
                          <Divider />
                          <MenuItem
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === currentUser?.id}
                            sx={{ color: 'danger.500' }}
                          >
                            🗑️ Delete
                          </MenuItem>
                        </Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        </Card>

        {/* Create User Modal */}
        <Modal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 500, maxWidth: 600 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Create New User
            </Typography>

            <form onSubmit={handleCreateUser}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Password</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={formData.role}
                    onChange={(e, value) => setFormData({ ...formData, role: value })}
                  >
                    <Option value="viewer">Viewer</Option>
                    <Option value="editor">Editor</Option>
                    <Option value="manager">Manager</Option>
                    <Option value="admin">Administrator</Option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Permissions</FormLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {availablePermissions.map((permission) => (
                      <Box key={permission.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Checkbox
                          checked={formData.permissions.includes(permission.id)}
                          onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                          sx={{ mt: 0.5 }}
                        />
                        <Box>
                          <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                            {permission.label}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                            {permission.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </FormControl>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsCreateModalOpen(false)}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading('createUser')}
                    sx={{ flex: 1 }}
                  >
                    Create User
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>

        {/* Edit User Modal */}
        <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <ModalDialog sx={{ minWidth: 500, maxWidth: 600 }}>
            <ModalClose />
            <Typography level="h4" sx={{ mb: 2 }}>
              Edit User
            </Typography>

            <form onSubmit={handleEditUser}>
              <Stack spacing={3}>
                {error && (
                  <Alert color="danger" variant="soft">
                    {error}
                  </Alert>
                )}

                <FormControl>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>New Password (leave blank to keep current)</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter new password (optional)"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={formData.role}
                    onChange={(e, value) => setFormData({ ...formData, role: value })}
                  >
                    <Option value="viewer">Viewer</Option>
                    <Option value="editor">Editor</Option>
                    <Option value="manager">Manager</Option>
                    <Option value="admin">Administrator</Option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Permissions</FormLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {availablePermissions.map((permission) => (
                      <Box key={permission.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Checkbox
                          checked={formData.permissions.includes(permission.id)}
                          onChange={(e) => handlePermissionChange(permission.id, e.target.checked)}
                          sx={{ mt: 0.5 }}
                        />
                        <Box>
                          <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                            {permission.label}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'neutral.600' }}>
                            {permission.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </FormControl>

                <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditModalOpen(false)}
                    sx={{ flex: 1 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={isLoading('editUser')}
                    sx={{ flex: 1 }}
                  >
                    Update User
                  </Button>
                </Stack>
              </Stack>
            </form>
          </ModalDialog>
        </Modal>
      </Box>
    </MainLayout>
  );
};

export default UserManagementPage;